import React, { useEffect, useMemo, useState } from 'react';
import {
  DashboardTab,
  FileData,
  RequiredFiles,
  SavedTest,
  Step,
  StudentDiagnosticReport,
  User,
} from '../types';
import CopilotFileUpload from './CopilotFileUpload';
import ResultsView from './ResultsView';
import { gradeWithBackend } from '../services/evaluationApi';
import { isBackendAuthEnabled } from '../services/authService';
import { db } from '../services/mockDb';
import { toLegacyGradingReport } from '../services/reportAdapters';
import { deleteSavedTest, loadSavedTests, saveTest, toFileData } from '../services/testStore';

interface TeacherDashboardProps {
  user: User;
}

const GRADE_OPTIONS = ['5', '6', '7', '8', '9', '10'];

const tabLabels: Record<DashboardTab, string> = {
  overview: 'Overview',
  tests: 'Tests',
  analytics: 'Analytics',
};

const TeacherDashboard: React.FC<TeacherDashboardProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<DashboardTab>('tests');
  const [savedTests, setSavedTests] = useState<SavedTest[]>(() => loadSavedTests());
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);

  const [testName, setTestName] = useState('');
  const [gradeLevel, setGradeLevel] = useState('10');
  const [subject, setSubject] = useState('Science');
  const [questionPaper, setQuestionPaper] = useState<FileData[]>([]);
  const [answerKey, setAnswerKey] = useState<FileData[]>([]);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  const [answerSheets, setAnswerSheets] = useState<FileData[]>([]);
  const [step, setStep] = useState<Step>(Step.UPLOAD);
  const [error, setError] = useState<string | null>(null);
  const [gradingReports, setGradingReports] = useState<StudentDiagnosticReport[]>([]);
  const [progress, setProgress] = useState({ completed: 0, total: 0, phase: 'Waiting to start' });

  const selectedTest = useMemo(
    () => savedTests.find((test) => test.id === selectedTestId) ?? null,
    [savedTests, selectedTestId],
  );

  useEffect(() => {
    setSavedTests(loadSavedTests());
  }, []);

  const analytics = db.getClassAnalytics();
  const recentAssessments = db.getAssessments();

  const resetGradingState = () => {
    setAnswerSheets([]);
    setGradingReports([]);
    setStep(Step.UPLOAD);
    setProgress({ completed: 0, total: 0, phase: 'Waiting to start' });
    setError(null);
  };

  const handleCreateTest = (event: React.FormEvent) => {
    event.preventDefault();
    setCreateError(null);
    setCreateSuccess(null);

    if (!testName.trim()) {
      setCreateError('Give your test a name.');
      return;
    }
    if (!subject.trim()) {
      setCreateError('Enter a subject.');
      return;
    }
    if (!questionPaper[0] || !answerKey[0]) {
      setCreateError('Upload both the question paper and answer key.');
      return;
    }

    const created = saveTest({
      name: testName.trim(),
      gradeLevel,
      subject: subject.trim(),
      questionPaper: questionPaper[0],
      answerKey: answerKey[0],
    });

    setSavedTests(loadSavedTests());
    setTestName('');
    setQuestionPaper([]);
    setAnswerKey([]);
    setCreateSuccess(`“${created.name}” is ready. Add answer sheets whenever you like.`);
  };

  const handleDeleteTest = (testId: string) => {
    deleteSavedTest(testId);
    setSavedTests(loadSavedTests());
    if (selectedTestId === testId) {
      setSelectedTestId(null);
      resetGradingState();
    }
  };

  const handleGradeTest = async () => {
    if (!selectedTest) return;
    if (answerSheets.length === 0) {
      setError('Upload at least one answer sheet.');
      return;
    }

    if (!isBackendAuthEnabled()) {
      setError('Secure backend grading is not configured. Set API_BASE_URL before building the website.');
      return;
    }

    setError(null);
    setStep(Step.PROCESSING);
    setProgress({ completed: 0, total: answerSheets.length, phase: 'Submitting grading job' });

    const qp = toFileData(selectedTest.questionPaper);
    const ak = toFileData(selectedTest.answerKey);
    const files: RequiredFiles = {
      questionPaper: qp,
      answerKey: ak,
      rubric: ak,
      studentSheet: answerSheets,
    };

    try {
      const reports = await gradeWithBackend(
        files,
        selectedTest.gradeLevel,
        selectedTest.subject,
        (completed, total, phase) => {
          setProgress({ completed, total, phase });
        },
      );

      reports.forEach((report) => db.saveAssessment(report));
      setGradingReports(reports);
      setProgress({ completed: reports.length, total: reports.length, phase: 'Completed' });
      setStep(Step.RESULTS);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Grading failed.');
      setStep(Step.UPLOAD);
      setProgress({ completed: 0, total: 0, phase: 'Failed' });
    }
  };

  const renderTabs = () => (
    <div className="inline-flex rounded-full bg-white/80 p-1 border border-stone-200 shadow-sm">
      {(Object.keys(tabLabels) as DashboardTab[]).map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => {
            setActiveTab(tab);
            if (tab !== 'tests') {
              setSelectedTestId(null);
              resetGradingState();
            }
          }}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
            activeTab === tab
              ? 'bg-accent text-white shadow-sm'
              : 'text-stone-700 hover:text-stone-900'
          }`}
        >
          {tabLabels[tab]}
        </button>
      ))}
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-card">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Class average</div>
          <div className="mt-3 font-serif text-4xl font-bold text-stone-900">{analytics.average}%</div>
        </div>
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-card">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Papers graded</div>
          <div className="mt-3 font-serif text-4xl font-bold text-stone-900">{analytics.count}</div>
        </div>
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-card">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Saved tests</div>
          <div className="mt-3 font-serif text-4xl font-bold text-stone-900">{savedTests.length}</div>
        </div>
      </div>

      <div className="rounded-3xl border border-stone-200 bg-white shadow-card overflow-hidden">
        <div className="border-b border-stone-100 px-6 py-4">
          <h3 className="font-serif text-2xl font-bold text-stone-900">Recent graded work</h3>
        </div>
        {recentAssessments.length === 0 ? (
          <div className="px-6 py-16 text-center text-stone-500">
            No graded papers yet. Create a test, then add answer sheets to grade.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                <tr>
                  <th className="px-6 py-3">Student</th>
                  <th className="px-6 py-3">Subject</th>
                  <th className="px-6 py-3">Score</th>
                  <th className="px-6 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {recentAssessments.map((assessment) => (
                  <tr key={assessment.id}>
                    <td className="px-6 py-4 font-medium">{assessment.studentName}</td>
                    <td className="px-6 py-4 text-stone-600">{assessment.subject}</td>
                    <td className="px-6 py-4 font-semibold text-accent">
                      {Math.round((assessment.score / assessment.maxScore) * 100)}%
                    </td>
                    <td className="px-6 py-4 text-stone-500">
                      {new Date(assessment.dateGraded).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-card">
      <h3 className="font-serif text-2xl font-bold text-stone-900">Class analytics</h3>
      <p className="mt-2 text-stone-600">
        Track how your class is performing across graded answer sheets.
      </p>
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl bg-accent/5 p-6">
          <div className="text-sm text-stone-600">Average score</div>
          <div className="mt-2 font-serif text-3xl font-bold text-stone-900">{analytics.average}%</div>
        </div>
        <div className="rounded-2xl bg-accent/5 p-6">
          <div className="text-sm text-stone-600">Assessments completed</div>
          <div className="mt-2 font-serif text-3xl font-bold text-stone-900">{analytics.count}</div>
        </div>
      </div>
      {recentAssessments.length > 0 && (
        <div className="mt-8 space-y-3">
          {recentAssessments.map((assessment) => (
            <div
              key={assessment.id}
              className="flex items-center justify-between rounded-2xl border border-stone-200 px-5 py-4"
            >
              <div>
                <div className="font-medium text-stone-900">{assessment.studentName}</div>
                <div className="text-sm text-stone-500">{assessment.subject}</div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-accent">
                  {Math.round((assessment.score / assessment.maxScore) * 100)}%
                </div>
                <div className="text-xs text-stone-500">
                  {new Date(assessment.dateGraded).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderTestsList = () => (
    <div className="rounded-3xl border border-stone-200 bg-white p-6 md:p-8 shadow-card min-h-[520px]">
      <h3 className="font-serif text-2xl font-bold text-stone-900">Your tests</h3>

      {savedTests.length === 0 ? (
        <div className="flex h-full min-h-[420px] flex-col items-center justify-center px-6 text-center">
          <div className="mb-4 text-accent">
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <p className="font-serif text-xl font-bold text-stone-900">No tests yet</p>
          <p className="mt-2 max-w-sm text-sm text-stone-500">
            Create a test on the left, then add answer sheets to it whenever you like.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {savedTests.map((test) => (
            <button
              key={test.id}
              type="button"
              onClick={() => {
                setSelectedTestId(test.id);
                resetGradingState();
              }}
              className={`w-full rounded-2xl border px-5 py-4 text-left transition-all ${
                selectedTestId === test.id
                  ? 'border-accent bg-accent/5 shadow-sm'
                  : 'border-stone-200 hover:border-accent/40 hover:bg-stone-50'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-stone-900">{test.name}</div>
                  <div className="mt-1 text-sm text-stone-500">
                    Grade {test.gradeLevel} · {test.subject}
                  </div>
                  <div className="mt-1 text-xs text-stone-400">
                    Created {new Date(test.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteTest(test.id);
                  }}
                  className="text-xs font-medium text-stone-400 hover:text-red-600"
                >
                  Delete
                </button>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderCreateTest = () => (
    <form
      onSubmit={handleCreateTest}
      className="rounded-3xl border border-stone-200 bg-white p-6 md:p-8 shadow-card"
    >
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-400">Create a test</div>

      <div className="mt-6 space-y-6">
        <div>
          <label className="mb-2 block text-sm font-medium text-stone-700">Test name</label>
          <input
            type="text"
            value={testName}
            onChange={(event) => setTestName(event.target.value)}
            placeholder="e.g. Grade 10 Maths · Midterm 1"
            className="w-full rounded-xl border border-stone-300 px-4 py-3 text-stone-900 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-stone-700">Grade</label>
            <select
              value={gradeLevel}
              onChange={(event) => setGradeLevel(event.target.value)}
              className="w-full rounded-xl border border-stone-300 px-4 py-3 text-stone-900 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
            >
              {GRADE_OPTIONS.map((grade) => (
                <option key={grade} value={grade}>
                  Grade {grade}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-stone-700">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              className="w-full rounded-xl border border-stone-300 px-4 py-3 text-stone-900 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-stone-700">Question paper</label>
          <CopilotFileUpload
            title="The exam questions"
            subtitle="PDF or image"
            files={questionPaper}
            onUpdate={setQuestionPaper}
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-stone-700">Answer key</label>
          <CopilotFileUpload
            title="Correct answers"
            subtitle="PDF or image"
            files={answerKey}
            onUpdate={setAnswerKey}
          />
        </div>
      </div>

      {createError && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {createError}
        </div>
      )}
      {createSuccess && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {createSuccess}
        </div>
      )}

      <button
        type="submit"
        className="mt-6 w-full rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-white transition hover:bg-accent-dark"
      >
        Save test
      </button>
    </form>
  );

  const renderGradingPanel = () => {
    if (!selectedTest) return null;

    if (step === Step.RESULTS && gradingReports.length > 0) {
      return (
        <div className="mt-8 space-y-6">
          <div className="rounded-3xl border border-emerald-200 bg-emerald-50 px-6 py-4 text-emerald-800">
            Grading complete for {gradingReports.length} student{gradingReports.length === 1 ? '' : 's'}.
          </div>
          <ResultsView
            reports={gradingReports.map(toLegacyGradingReport)}
            onReset={() => {
              resetGradingState();
            }}
          />
        </div>
      );
    }

    if (step === Step.PROCESSING) {
      return (
        <div className="mt-8 rounded-3xl border border-stone-200 bg-white p-10 text-center shadow-card">
          <div className="mx-auto mb-6 h-16 w-16 rounded-full border-4 border-stone-200 border-t-accent animate-spin" />
          <h3 className="font-serif text-2xl font-bold text-stone-900">Grading answer sheets</h3>
          <p className="mt-2 text-stone-600">{progress.phase}</p>
          <div className="mx-auto mt-6 max-w-md">
            <div className="h-2 overflow-hidden rounded-full bg-stone-100">
              <div
                className="h-full rounded-full bg-accent transition-all"
                style={{
                  width: `${
                    progress.total > 0
                      ? Math.max((Math.min(progress.completed, progress.total) / progress.total) * 100, 8)
                      : 8
                  }%`,
                }}
              />
            </div>
            <p className="mt-2 text-sm text-stone-500">
              {progress.total > 0 ? `${progress.completed} / ${progress.total}` : 'Starting…'}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-8 rounded-3xl border border-stone-200 bg-white p-6 md:p-8 shadow-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-400">
              Add answer sheets
            </div>
            <h3 className="mt-2 font-serif text-2xl font-bold text-stone-900">{selectedTest.name}</h3>
            <p className="mt-1 text-sm text-stone-500">
              Grade {selectedTest.gradeLevel} · {selectedTest.subject}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setSelectedTestId(null);
              resetGradingState();
            }}
            className="text-sm font-medium text-stone-500 hover:text-stone-900"
          >
            Close
          </button>
        </div>

        <div className="mt-6">
          <CopilotFileUpload
            title="Student answer sheets"
            subtitle="Upload one or more PDFs or images"
            files={answerSheets}
            multiple
            onUpdate={setAnswerSheets}
          />
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleGradeTest}
          disabled={answerSheets.length === 0}
          className="mt-6 rounded-xl bg-accent px-6 py-3 text-sm font-semibold text-white transition hover:bg-accent-dark disabled:cursor-not-allowed disabled:bg-stone-300"
        >
          Start grading
        </button>
      </div>
    );
  };

  const pageTitle =
    activeTab === 'overview' ? 'Overview' : activeTab === 'analytics' ? 'Analytics' : 'Tests';
  const pageSubtitle =
    activeTab === 'tests'
      ? 'Set up a test once, then add answer sheets to it any time.'
      : activeTab === 'overview'
        ? `Welcome back, ${user.name.split(' ')[0] || user.name}.`
        : 'See how your class is performing over time.';

  return (
    <div>
      <header className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-stone-900">{pageTitle}</h1>
          <p className="mt-2 max-w-2xl text-stone-600">{pageSubtitle}</p>
        </div>
        {renderTabs()}
      </header>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'analytics' && renderAnalytics()}

      {activeTab === 'tests' && (
        <>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {renderCreateTest()}
            {renderTestsList()}
          </div>
          {renderGradingPanel()}
        </>
      )}
    </div>
  );
};

export default TeacherDashboard;
